import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PDFDownloadLink } from "@react-pdf/renderer";
import QRCode from "qrcode";
import { AuthContext } from "../../auth/auth-context";
import BusinessForm from "../../forms/BusinessForm";
import MenuForm from "../../forms/MenuForm";
import LocationForm from "../../forms/LocationForm";
import ImageUpload from "../../forms/ImageUpload";
import AccountLoading from "./AccountLoading";
import LocationMenuPDF from "../../components/LocationMenuPDF";

// ── Menu row accent colours (cycles by index) ──────────────────────────────
const ACCENT_COLORS = ["#2C6B4F", "#C084FC", "#F59E0B", "#60A5FA", "#F87171"];

// ── LocationRow ──────────────────────────────────────────────────────────────
const LocationRow = ({
  location,
  businessItem,
  fetchBusinesses,
  toggleLocationFormOverlay,
  canEdit,
}) => {
  const [qrDataURL, setQrDataURL] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  const locationURL = `${process.env.REACT_APP_FRONTEND_URL}/${businessItem.domainUri}/${location.location.uri}`;
  const businessURL = `${process.env.REACT_APP_FRONTEND_URL}/${businessItem.domainUri}`;
  const what3wordsURL = `https://what3words.com/${location.location.what3words}`;
  const downloadName = `${businessItem.domainUri}_${location.location.uri}`;

  useEffect(() => {
    QRCode.toDataURL(locationURL, { width: 400, margin: 2, errorCorrectionLevel: "H" })
      .then(setQrDataURL)
      .catch(console.error);
  }, [locationURL]);

  const pdfDetails = {
    id: location._id,
    name: location.location.name,
    uri: location.location.uri,
    businessURL,
    businessName: businessItem.businessName,
    qrCode: qrDataURL,
  };

  const deleteLocation = async () => {
    setConfirmingDelete(false);
    setDeleteInProgress(true);
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/locations/${businessItem._id}/${location._id}`,
      { credentials: "include", method: "DELETE" }
    );
    if (response.ok) fetchBusinesses();
    setDeleteInProgress(false);
  };

  const ghostBtn = {
    padding: "4px 8px",
    borderRadius: "6px",
    fontSize: "11px",
    fontWeight: 600,
    background: "none",
    border: "1px solid var(--border)",
    color: "var(--ink-soft)",
    cursor: "pointer",
    fontFamily: "inherit",
  };
  const dangerBtn = { ...ghostBtn, border: "1px solid #FBCCC0", color: "var(--accent)" };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "12px",
        padding: "10px 12px",
        background: "var(--cream)",
        border: "1px solid var(--border)",
        borderRadius: "8px",
        marginBottom: "6px",
      }}
    >
      {/* Location details */}
      <div style={{ minWidth: 0, flex: 1 }}>
        <a
          href={locationURL}
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: "13px", fontWeight: 600, color: "var(--ink)", textDecoration: "none" }}
        >
          📍 {location.location.name}
        </a>
        <div style={{ fontSize: "11px", color: "var(--ink-muted)", marginTop: "2px" }}>
          {location.location.city}, {location.location.country}
        </div>
        {location.location.what3words && (
          <a
            href={what3wordsURL}
            target="_blank"
            rel="noreferrer"
            title={`Find ${businessItem.businessName}, ${location.location.name} on what3words`}
            style={{ fontSize: "11px", color: "var(--ink-muted)", textDecoration: "none" }}
          >
            <span style={{ color: "#E11D48" }}>/// </span>
            {location.location.what3words}
          </a>
        )}

        {canEdit && (
          <div style={{ display: "flex", gap: "4px", marginTop: "8px", flexWrap: "wrap", alignItems: "center" }}>
            <button
              onClick={() =>
                toggleLocationFormOverlay("update", businessItem._id, location._id, location)
              }
              style={ghostBtn}
            >
              Edit
            </button>
            {confirmingDelete ? (
              <>
                <span style={{ fontSize: "11px", color: "var(--ink-muted)" }}>Sure?</span>
                <button onClick={deleteLocation} style={dangerBtn}>
                  {deleteInProgress ? "Deleting…" : "Yes, delete"}
                </button>
                <button onClick={() => setConfirmingDelete(false)} style={ghostBtn}>
                  Cancel
                </button>
              </>
            ) : (
              <button onClick={() => setConfirmingDelete(true)} style={dangerBtn}>
                Delete
              </button>
            )}
          </div>
        )}
      </div>

      {/* QR code + PDF download */}
      {qrDataURL && (
        <div style={{ textAlign: "center", flexShrink: 0 }}>
          <PDFDownloadLink
            document={<LocationMenuPDF details={pdfDetails} />}
            fileName={`${downloadName}-qr-menu.pdf`}
            style={{ textDecoration: "none" }}
          >
            <img
              src={qrDataURL}
              alt={`QR code for ${location.location.name}`}
              style={{ width: "52px", height: "52px", display: "block" }}
            />
            <span
              style={{
                fontSize: "10px",
                color: "var(--brand)",
                fontWeight: 600,
                marginTop: "3px",
                display: "block",
              }}
            >
              Download PDF
            </span>
          </PDFDownloadLink>
        </div>
      )}
    </div>
  );
};

// ── MenuRow ─────────────────────────────────────────────────────────────────
const MenuRow = ({
  menu,
  accentColor,
  businessItem,
  fetchBusinesses,
  toggleMenuFormOverlay,
  toggleLocationFormOverlay,
  canEdit,
  atLocationTrialLimit,
}) => {
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  const deleteMenu = async () => {
    if (!window.confirm("Are you sure you want to delete this menu?")) return;
    setDeleteInProgress(true);
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/menu/${businessItem._id}/${menu._id}`,
      { credentials: "include", method: "DELETE" }
    );
    if (response.ok) fetchBusinesses();
    setDeleteInProgress(false);
  };

  const locations = Array.isArray(businessItem.locations) ? businessItem.locations : [];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        background: "var(--white)",
        border: "1px solid var(--border)",
        borderRadius: "10px",
        overflow: "hidden",
        marginBottom: "8px",
        boxShadow: "var(--shadow)",
      }}
    >
      {/* Coloured accent bar */}
      <div style={{ width: "4px", flexShrink: 0, background: accentColor }} />

      <div style={{ flex: 1, padding: "14px 16px" }}>
        {/* Top row: name + actions */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{ fontSize: "14px", fontWeight: 600, color: "var(--ink)" }}
            >
              {menu.menuName}
            </div>
            <div style={{ fontSize: "11px", color: "var(--ink-muted)", marginTop: "2px" }}>
              tastebud.ee/{businessItem.domainUri}
            </div>
          </div>

          {canEdit && (
            <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
              <Link
                to={`/account/menu/${businessItem._id}/${menu._id}`}
                style={{
                  display: "inline-flex",
                  padding: "5px 10px",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: 600,
                  background: "none",
                  border: "1px solid var(--border)",
                  color: "var(--ink-soft)",
                  textDecoration: "none",
                }}
              >
                View
              </Link>
              <button
                onClick={() =>
                  toggleMenuFormOverlay("update", businessItem._id, menu._id, menu)
                }
                style={{
                  padding: "5px 10px",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: 600,
                  background: "none",
                  border: "1px solid var(--border)",
                  color: "var(--ink-soft)",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Edit
              </button>
              <button
                onClick={deleteMenu}
                style={{
                  padding: "5px 10px",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: 600,
                  background: "none",
                  border: "1px solid #FBCCC0",
                  color: "var(--accent)",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {deleteInProgress ? "…" : "Delete"}
              </button>
            </div>
          )}
        </div>

        {/* Locations section */}
        <div
          style={{
            marginTop: "10px",
            paddingTop: "10px",
            borderTop: "1px solid var(--border)",
          }}
        >
          {/* Locations header row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: locations.length > 0 ? "8px" : "6px",
            }}
          >
            <span
              style={{
                fontSize: "11px",
                fontWeight: 700,
                color: "var(--ink-muted)",
                textTransform: "uppercase",
                letterSpacing: "0.07em",
              }}
            >
              Locations
            </span>
            {canEdit && !atLocationTrialLimit && locations.length > 0 && (
              <button
                onClick={() => toggleLocationFormOverlay("create", businessItem._id)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "3px 8px",
                  borderRadius: "20px",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "var(--brand)",
                  background: "var(--brand-light)",
                  border: "1px dashed var(--brand-mid)",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                + Add location
              </button>
            )}
          </div>

          {/* Location cards */}
          {locations.length > 0 ? (
            locations.map((loc) => (
              <LocationRow
                key={loc._id}
                location={loc}
                businessItem={businessItem}
                fetchBusinesses={fetchBusinesses}
                toggleLocationFormOverlay={toggleLocationFormOverlay}
                canEdit={canEdit}
              />
            ))
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "12px", color: "var(--ink-muted)", fontStyle: "italic" }}>
                No locations yet —
              </span>
              {canEdit && !atLocationTrialLimit && (
                <button
                  onClick={() => toggleLocationFormOverlay("create", businessItem._id)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "3px 8px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "var(--brand)",
                    background: "var(--brand-light)",
                    border: "1px dashed var(--brand-mid)",
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  + Add first location
                </button>
              )}
            </div>
          )}

          {atLocationTrialLimit && (
            <p style={{ fontSize: "12px", color: "#92400E", marginTop: "6px" }}>
              Trial allows 1 location.{" "}
              <Link to="/account/subscription" style={{ color: "inherit", fontWeight: 600 }}>
                Upgrade
              </Link>{" "}
              for more.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ── BizCard ──────────────────────────────────────────────────────────────────
const BizCard = ({
  item,
  fetchBusinesses,
  setEditBusinessInitialValues,
  toggleBusinessFormOverlay,
  toggleMenuFormOverlay,
  toggleLocationFormOverlay,
  changeImage,
  canEdit,
  isTrialOnly,
}) => {
  const menusCount = Array.isArray(item?.menus) ? item.menus.length : 0;
  const locationsCount = Array.isArray(item?.locations) ? item.locations.length : 0;
  const atMenuTrialLimit = isTrialOnly && menusCount >= 1;
  const atLocationTrialLimit = isTrialOnly && locationsCount >= 1;

  const deleteBusiness = async (id) => {
    if (!window.confirm("Are you sure you want to delete this business?"))
      return;
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/business/${id}`,
      { credentials: "include", method: "DELETE" }
    );
    if (response.ok) fetchBusinesses();
  };

  const editBusiness = (id) => {
    setEditBusinessInitialValues({ id, data: item });
    toggleBusinessFormOverlay("update");
  };

  const ghostBtn = {
    padding: "5px 10px",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: 600,
    background: "none",
    border: "1px solid var(--border)",
    color: "var(--ink-soft)",
    cursor: "pointer",
    fontFamily: "inherit",
  };

  const dangerBtn = {
    ...ghostBtn,
    border: "1px solid #FBCCC0",
    color: "var(--accent)",
  };

  return (
    <div
      style={{
        background: "var(--white)",
        border: "1px solid var(--border)",
        borderRadius: "12px",
        padding: "20px",
        marginBottom: "16px",
        boxShadow: "var(--shadow)",
      }}
    >
      {/* Business header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: "16px",
          gap: "12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          {/* Logo / image */}
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #6EE7B7, #059669)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "22px",
              overflow: "hidden",
              flexShrink: 0,
              position: "relative",
              cursor: canEdit ? "pointer" : "default",
            }}
            onClick={() => canEdit && changeImage("business", item._id)}
            title={canEdit ? "Change image" : undefined}
          >
            {item.businessImage ? (
              <img
                src={decodeURIComponent(item.businessImage)}
                alt={item.businessName}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "10px",
                }}
              />
            ) : (
              "🍽️"
            )}
          </div>

          <div>
            <div
              style={{
                fontSize: "16px",
                fontWeight: 600,
                color: "var(--ink)",
              }}
            >
              {item.businessName}
            </div>
            <a
              href={`${process.env.REACT_APP_FRONTEND_URL}/${item.domainUri}`}
              target="_blank"
              rel="noreferrer"
              style={{
                fontSize: "12px",
                color: "var(--brand)",
                textDecoration: "none",
              }}
            >
              tastebud.ee/{item.domainUri}
            </a>
            {item.aboutCopy && (
              <div
                style={{
                  fontSize: "12px",
                  color: "var(--ink-muted)",
                  marginTop: "3px",
                }}
              >
                {item.aboutCopy}
              </div>
            )}
          </div>
        </div>

        {canEdit && (
          <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
            <button onClick={() => editBusiness(item._id)} style={ghostBtn}>
              Edit
            </button>
            <button
              onClick={() => deleteBusiness(item._id)}
              style={dangerBtn}
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Separator */}
      <div
        style={{
          height: "1px",
          background: "var(--border)",
          marginBottom: "16px",
        }}
      />

      {/* Menus label */}
      <div
        style={{
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--ink-muted)",
          marginBottom: "10px",
        }}
      >
        Menus
      </div>

      {/* Menu rows */}
      {Array.isArray(item.menus) && item.menus.length > 0 ? (
        item.menus.map((menu, idx) => (
          <MenuRow
            key={menu._id}
            menu={menu}
            accentColor={ACCENT_COLORS[idx % ACCENT_COLORS.length]}
            businessItem={item}
            fetchBusinesses={fetchBusinesses}
            toggleMenuFormOverlay={toggleMenuFormOverlay}
            toggleLocationFormOverlay={toggleLocationFormOverlay}
            canEdit={canEdit}
            atLocationTrialLimit={atLocationTrialLimit}
          />
        ))
      ) : (
        <p
          style={{
            fontSize: "13px",
            color: "var(--ink-muted)",
            fontStyle: "italic",
            marginBottom: "10px",
          }}
        >
          No menus yet.
        </p>
      )}

      {/* Add menu button */}
      {canEdit && !atMenuTrialLimit && (
        <button
          onClick={() => toggleMenuFormOverlay("create", item._id)}
          style={{
            width: "100%",
            padding: "9px",
            border: "1px dashed var(--border)",
            borderRadius: "8px",
            background: "none",
            fontSize: "12px",
            fontWeight: 600,
            color: "var(--ink-muted)",
            cursor: "pointer",
            marginTop: "10px",
            fontFamily: "inherit",
            transition: "all 0.15s",
          }}
        >
          + Add menu to this business
        </button>
      )}
      {isTrialOnly && atMenuTrialLimit && (
        <p style={{ fontSize: "12px", color: "#92400E", marginTop: "8px" }}>
          Trial allows 1 menu.{" "}
          <Link
            to="/account/subscription"
            style={{ color: "inherit", fontWeight: 600 }}
          >
            Upgrade
          </Link>{" "}
          for more.
        </p>
      )}
    </div>
  );
};

// ── AllergenMenus page ───────────────────────────────────────────────────────
const AllergenMenus = () => {
  const { authState } = useContext(AuthContext);

  const [myBusinessLoading, setMyBusinessLoading] = useState(true);
  const [myBusinessDetails, setMyBusinessDetails] = useState([]);

  // Business form
  const [isBusinessFormOverlayVisible, setIsBusinessFormOverlayVisible] =
    useState(false);
  const [businessFormMode, setBusinessFormMode] = useState("create");
  const [editBusinessInitialValues, setEditBusinessInitialValues] = useState({
    id: "",
    data: {},
  });

  // Menu form
  const [isMenuFormOverlayVisible, setIsMenuFormOverlayVisible] =
    useState(false);
  const [menuFormMode, setMenuFormMode] = useState("create");
  const [updateMenuInitialValues, setupdateMenuInitialValues] = useState({
    menuBusinessId: "",
    menuId: "",
    menuItem: {},
  });
  const [menuBusinessId, setMenuBusinessId] = useState("");

  // Location form
  const [isLocationFormOverlayVisible, setIsLocationFormOverlayVisible] =
    useState(false);
  const [locationFormMode, setLocationFormMode] = useState("create");
  const [updateLocationInitialValues, setUpdateLocationInitialValues] =
    useState({ id: "", data: {} });
  const [locationBusinessId, setLocationBusinessId] = useState("");

  // Image upload
  const [isImageUpdateFormVisible, setIsImageUpdateFormVisible] =
    useState(false);
  const [imageUploadDetails, setImageUploadDetails] = useState({});

  // ── Data fetching ────────────────────────────────────────────────────────
  const fetchBusinesses = async () => {
    setMyBusinessLoading(true);
    try {
      const response = await fetch(
        process.env.REACT_APP_BACKEND_URL + "/business",
        { credentials: "include", method: "GET" }
      );
      const data = await response.json();
      setMyBusinessDetails(data);
      setMyBusinessLoading(false);
    } catch (error) {
      console.error(`Something went wrong: ${error.message}`);
    }
  };

  useEffect(() => {
    fetchBusinesses();
  }, []);

  // ── Business handlers ────────────────────────────────────────────────────
  const toggleBusinessFormOverlay = (mode = "create") => {
    if (mode === "create") {
      setBusinessFormMode("create");
      setEditBusinessInitialValues({ id: "", data: {} });
    } else {
      setBusinessFormMode("update");
    }
    setIsBusinessFormOverlayVisible((v) => !v);
  };

  const createBusiness = async (formData) => {
    try {
      const response = await fetch(
        process.env.REACT_APP_BACKEND_URL + "/business",
        { credentials: "include", method: "POST", body: formData }
      );
      if (!response.ok) throw new Error(response.statusText);
      return await response.json();
    } catch (e) {
      console.log(e.message);
    }
  };

  const updateBusiness = async (id, formData) => {
    try {
      const response = await fetch(
        process.env.REACT_APP_BACKEND_URL + "/business/" + id,
        {
          credentials: "include",
          method: "PATCH",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(formData),
        }
      );
      if (!response.ok) throw new Error(response.statusText);
      return await response.json();
    } catch (e) {
      console.log(e.message);
    }
  };

  // ── Menu handlers ────────────────────────────────────────────────────────
  const toggleMenuFormOverlay = (
    mode = "create",
    businessId = "",
    menuId = "",
    menuItem = {}
  ) => {
    setMenuFormMode(mode);
    setupdateMenuInitialValues({ menuBusinessId: businessId, menuId, menuItem });
    setMenuBusinessId(businessId);
    setIsMenuFormOverlayVisible((v) => !v);
  };

  const createMenu = async (data, businessId) => {
    const response = await fetch(
      process.env.REACT_APP_BACKEND_URL + "/menu/" + businessId,
      {
        credentials: "include",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );
    if (response.ok) {
      fetchBusinesses();
      setMenuBusinessId("");
    }
  };

  const updateMenu = async (businessId, menuId, data) => {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/menu/${businessId}/${menuId}`,
      {
        credentials: "include",
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );
    if (response.ok) {
      fetchBusinesses();
      setMenuBusinessId("");
    }
  };

  // ── Location handlers ────────────────────────────────────────────────────
  const toggleLocationFormOverlay = (
    mode = "create",
    businessId = "",
    locationId = "",
    locationItem
  ) => {
    setLocationFormMode(mode);
    setLocationBusinessId(businessId);
    setUpdateLocationInitialValues({
      locationBusinessId: businessId,
      locationId,
      locationItem,
    });
    setIsLocationFormOverlayVisible((v) => !v);
  };

  const createLocation = async (data, businessId) => {
    const response = await fetch(
      process.env.REACT_APP_BACKEND_URL + "/locations/" + businessId,
      {
        credentials: "include",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );
    if (response.ok) {
      fetchBusinesses();
      setLocationBusinessId("");
    }
  };

  const updateLocation = async (businessId, locationId, data) => {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/locations/${businessId}/${locationId}`,
      {
        credentials: "include",
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );
    if (response.ok) fetchBusinesses();
  };

  // ── Image upload ─────────────────────────────────────────────────────────
  const changeImage = (type, businessId) => {
    setImageUploadDetails({ type, businessId });
    setIsImageUpdateFormVisible(true);
  };

  const closeAllOverlays = () => {
    setIsBusinessFormOverlayVisible(false);
    setIsMenuFormOverlayVisible(false);
    setIsLocationFormOverlayVisible(false);
    setIsImageUpdateFormVisible(false);
  };

  // ── Derived state ────────────────────────────────────────────────────────
  const isSubscribed = !!authState?.isSubscribed;
  const isTrialActive = !!authState?.isTrialActive;
  const trialDaysRemaining = authState?.trialDaysRemaining ?? 0;
  const isTrialOnly = isTrialActive && !isSubscribed;
  const businessCount = Array.isArray(myBusinessDetails)
    ? myBusinessDetails.length
    : 0;
  const atTrialBusinessLimit = isTrialOnly && businessCount >= 1;
  const canEdit = isSubscribed || isTrialActive;
  const canAddBusiness = isSubscribed || (isTrialActive && !atTrialBusinessLimit);

  // ── Overlay modal wrapper ─────────────────────────────────────────────────
  const Modal = ({ children }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-start z-50 overflow-y-auto">
      <div className="p-4 m-auto">{children}</div>
    </div>
  );

  if (myBusinessLoading) return <AccountLoading />;

  return (
    <div
      style={{
        background: "var(--cream)",
        minHeight: "100vh",
        padding: "28px 28px 72px",
      }}
    >
      <Helmet>
        <title>Allergen Menus | tastebud.ee</title>
        <meta name='robots' content='noindex' />
        <link rel='canonical' href='https://tastebud.ee/account/allergen-menus' />
      </Helmet>
      <div style={{ maxWidth: "900px", margin: "0 auto" }}>
        {/* Trial banner */}
        {isTrialActive && !isSubscribed && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
              padding: "12px 16px",
              borderRadius: "8px",
              marginBottom: "24px",
              background: "#FEF3C7",
              border: "1px solid #FDE68A",
              color: "#92400E",
            }}
          >
            <p style={{ fontSize: "13px", lineHeight: "1.5", margin: 0 }}>
              You have <strong>{trialDaysRemaining} days remaining</strong> on your trial.
            </p>
            <Link
              to="/account/subscription"
              style={{
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                padding: "6px 14px",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: 600,
                background: "#FFFFFF",
                color: "#1A1714",
                textDecoration: "none",
                border: "1px solid #FDE68A",
                whiteSpace: "nowrap",
              }}
            >
              Upgrade now
            </Link>
          </div>
        )}

        {/* Page heading row */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            marginBottom: "24px",
            gap: "16px",
          }}
        >
          <div>
            <h1
              style={{
                fontFamily: "'DM Serif Display', serif",
                fontSize: "22px",
                marginBottom: "4px",
                color: "var(--ink)",
              }}
            >
              Your Businesses
            </h1>
            <p style={{ fontSize: "13px", color: "var(--ink-muted)" }}>
              Each business can have multiple allergen menus, each served at
              different locations.
            </p>
          </div>

          {canAddBusiness && (
            <button
              onClick={() => toggleBusinessFormOverlay("create")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 16px",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: 600,
                background: "var(--brand)",
                color: "white",
                border: "none",
                cursor: "pointer",
                flexShrink: 0,
                fontFamily: "inherit",
              }}
            >
              + Add Business
            </button>
          )}
        </div>

        {/* Empty state */}
        {businessCount === 0 && (
          <div
            style={{
              background: "var(--white)",
              border: "1px solid var(--border)",
              borderRadius: "12px",
              padding: "40px 32px",
              textAlign: "center",
              boxShadow: "var(--shadow)",
            }}
          >
            <p
              style={{
                fontSize: "15px",
                fontWeight: 600,
                color: "var(--ink-soft)",
                marginBottom: "6px",
              }}
            >
              No businesses yet
            </p>
            <p
              style={{ fontSize: "13px", color: "var(--ink-muted)" }}
            >
              Add your first business to get started creating allergen menus.
            </p>
          </div>
        )}

        {/* Business cards */}
        {Array.isArray(myBusinessDetails) &&
          myBusinessDetails.map((biz) => (
            <BizCard
              key={biz._id}
              item={biz}
              fetchBusinesses={fetchBusinesses}
              setEditBusinessInitialValues={setEditBusinessInitialValues}
              toggleBusinessFormOverlay={toggleBusinessFormOverlay}
              toggleMenuFormOverlay={toggleMenuFormOverlay}
              toggleLocationFormOverlay={toggleLocationFormOverlay}
              changeImage={changeImage}
              canEdit={canEdit}
              isTrialOnly={isTrialOnly}
            />
          ))}

        {/* Add another business card */}
        {businessCount > 0 && (
          <div
            style={{
              border: "2px dashed var(--border)",
              borderRadius: "12px",
              padding: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "var(--ink-soft)",
                }}
              >
                Add another business
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "var(--ink-muted)",
                  marginTop: "2px",
                }}
              >
                Each business gets its own branded allergen menu page.
              </div>
            </div>

            {canAddBusiness ? (
              <button
                onClick={() => toggleBusinessFormOverlay("create")}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: 600,
                  background: "none",
                  border: "1px solid var(--border)",
                  color: "var(--ink-soft)",
                  cursor: "pointer",
                  flexShrink: 0,
                  fontFamily: "inherit",
                }}
              >
                + Add Business
              </button>
            ) : (
              <Link
                to="/account/subscription"
                style={{
                  fontSize: "12px",
                  color: "var(--brand)",
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                Upgrade to add more →
              </Link>
            )}
          </div>
        )}
      </div>

      {/* ── Overlay forms ── */}
      {isBusinessFormOverlayVisible && (
        <Modal>
          <BusinessForm
            closeForm={closeAllOverlays}
            fetchBusinesses={fetchBusinesses}
            mode={businessFormMode}
            createBusiness={createBusiness}
            updateBusiness={updateBusiness}
            editBusinessInitialValues={editBusinessInitialValues}
            setEditBusinessInitialValues={setEditBusinessInitialValues}
            locationBusinessId={locationBusinessId}
          />
        </Modal>
      )}

      {isMenuFormOverlayVisible && (
        <Modal>
          <MenuForm
            closeForm={closeAllOverlays}
            fetchBusinesses={fetchBusinesses}
            mode={menuFormMode}
            createMenu={createMenu}
            updateMenu={updateMenu}
            updateMenuInitialValues={updateMenuInitialValues}
            setupdateMenuInitialValues={setupdateMenuInitialValues}
            menuBusinessId={menuBusinessId}
            setMenuBusinessId={setMenuBusinessId}
          />
        </Modal>
      )}

      {isLocationFormOverlayVisible && (
        <Modal>
          <LocationForm
            closeForm={closeAllOverlays}
            fetchBusinesses={fetchBusinesses}
            mode={locationFormMode}
            createLocation={createLocation}
            updateLocation={updateLocation}
            updateLocationInitialValues={updateLocationInitialValues}
            setUpdateLocationInitialValues={setUpdateLocationInitialValues}
            locationBusinessId={locationBusinessId}
            setLocationBusinessId={setLocationBusinessId}
          />
        </Modal>
      )}

      {isImageUpdateFormVisible && (
        <Modal>
          <div className="bg-white">
            <ImageUpload
              imageUploadDetails={imageUploadDetails}
              closeUploadForm={closeAllOverlays}
              fetchBusinesses={fetchBusinesses}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AllergenMenus;
